import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { useModal, useToast } from '@pcmp/react-providers';
import Button from '@ingka/button';
import { Data } from '@pcmp/react-coworker-components';

import {
  CodeV2 as Code,
  usePutV2QrCodesBySlugMutation,
} from '@/redux/apis/codes-api';
import { CreateCodeForm } from '@/components/page-widgets/create-code/form';
import useSchema from '@/lib/hooks/yup';
import { generateSchema, IFormInputs } from '@/lib/schemas/create-code-form';
import { normalizeForm } from '@/lib/schemas/create-code-form/normalization';
import { useCode, useUser } from '../../create-code/context';
import { serializeForm } from '@/lib/schemas/create-code-form/serialization';
import CommunicationCarrierPrompt from '@/components/modals/communication-carrier-prompt';
import { useGetV1UsersByEmailQuery } from '@/redux/apis/frontend-api';

const { Card } = Data;

const SaveQrCodeButton: FC<{ code: Code }> = ({ code }) => {
  // hooks
  const { t } = useTranslation();
  const [updateQrCode] = usePutV2QrCodesBySlugMutation();
  const { pushToast } = useToast();
  const {
    formState: { isValid, isDirty },
    getValues,
  } = useFormContext();
  const { openModal } = useModal();
  const { data: user } = useGetV1UsersByEmailQuery({ email: code.user.email });

  const showStatusToast = (isError: boolean) => {
    const status = isError ? 'error' : 'success';
    const message = t(`page.codes.detail.messages.updateCode.${status}`);
    // @ts-ignore
    pushToast({ message });
  };

  // event handlers
  const updateCode = async () => {
    const data = getValues() as IFormInputs;
    try {
      const serializedData = serializeForm(data, user);
      const response = await updateQrCode({
        slug: code.slug as string,
        updateCodeV2: {
          ...serializedData,
        },
      });
      const isError = response && 'error' in response;
      showStatusToast(isError);
    } catch (err) {
      showStatusToast(true);
      return;
    }
  };

  const handleUpdateCodeButton = async () => {
    const data = getValues() as IFormInputs;

    if (!data.carrier.size?.length || !data.carrier.type?.length) {
      openModal({
        component: CommunicationCarrierPrompt,
        props: {
          confirm: updateCode,
        },
      });
    } else {
      updateCode();
    }
  };

  return (
    <Button
      className="w-[327px]"
      iconPosition="leading"
      text={t('page.codes.detail.updateCode')}
      type="emphasised"
      disabled={!isValid || !isDirty}
      onClick={handleUpdateCodeButton}
      data-testid="update-code-button"
      size="medium"
    />
  );
};

export const EditableQrCodeForm: FC = () => {
  // Form
  const resolver = useSchema(() => generateSchema());

  // Data
  const { code } = useCode();
  const { user } = useUser();
  const initialValues = code && normalizeForm(code);
  const isDefaultLocation =
    user?.country.toLowerCase() === code.location?.country?.toLowerCase() &&
    user?.cdsLocation === code.location?.site;
  const form = useForm<IFormInputs>({
    mode: 'all',
    resolver,
    defaultValues: {
      ...initialValues,
      defaultLocation: isDefaultLocation,
    },
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  });

  return (
    <FormProvider {...form}>
      <Card className="w-full mt-4">
        <CreateCodeForm />
      </Card>
      <div className="flex mt-4 justify-end">
        <SaveQrCodeButton code={code} />
      </div>
    </FormProvider>
  );
};
